/**
 * GetStartedChecklist Component
 *
 * Fixed-position collapsible widget that tracks real user onboarding actions.
 * Shows at bottom-right of screen when triggered and not completed.
 */

import { useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { X, Minimize2, Maximize2 } from 'lucide-react';
import {
  Button,
  Progress,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
} from '@scio3/ui';
import { WistiaDialog } from '@/components/shared';
import { useGetStartedChecklist } from '../hooks/useGetStartedChecklist';
import { GetStartedStep } from './GetStartedStep';
import { GETTING_STARTED_VIDEO_ID } from '../constants';

export function GetStartedChecklist() {
  const { t } = useTranslation();
  const { state, loading, completeChecklist } = useGetStartedChecklist();
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeStepId, setActiveStepId] = useState<string | null>(() => {
    const firstIncomplete = state.steps.find((s) => s.required && !s.completed);
    return firstIncomplete?.id ?? null;
  });
  const [showVideo, setShowVideo] = useState(false);

  // Find the next incomplete step (for collapsed view)
  const nextStep = useMemo(() => {
    return state.steps.find((s) => !s.completed) ?? null;
  }, [state.steps]);

  // Don't render if not visible (not triggered or already completed)
  if (!state.isVisible) {
    return null;
  }

  const handleComplete = async () => {
    await completeChecklist();
  };

  const toggleStep = (stepId: string) => {
    setActiveStepId((current) => (current === stepId ? null : stepId));
  };

  return (
    <div className="fixed bottom-24 right-6 z-40 w-80" data-testid="get-started-checklist">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        {/* Outer container — gray when expanded, white card when collapsed */}
        <div
          className={cn(
            'rounded-lg shadow-[0_0_8px_1px_rgba(0,0,0,0.12)]',
            isExpanded ? 'bg-muted pt-1' : 'border border-border bg-card pt-1 pb-2',
          )}
        >
          {/* Header — sits directly on bg, no card */}
          <div
            className={cn(
              'flex min-h-10 items-center gap-2 px-2 pb-1',
              !isExpanded && 'cursor-pointer select-none',
            )}
            onClick={() => !isExpanded && setIsExpanded(true)}
          >
            <CollapsibleTrigger asChild>
              <Button
                data-testid="checklist-collapse-toggle"
                variant="ghost"
                size="icon-sm"
                className="shrink-0 text-muted-foreground hover:text-foreground"
                aria-label={isExpanded ? t('getStarted.collapse') : t('getStarted.expand')}
              >
                {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <span className="shrink-0 text-sm font-semibold">{t('getStarted.title')}</span>
            {/* Progress bar — primary visual element in header */}
            <Progress value={state.progressPercent} className="h-2 flex-1 bg-muted-foreground/20" />
            {/* Close/dismiss button — only shown after all required steps are done */}
            {state.isComplete && (
              <Button
                data-testid="checklist-dismiss-btn"
                variant="ghost"
                size="icon-sm"
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  handleComplete();
                }}
                disabled={loading}
                aria-label={t('getStarted.dismiss')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Congrats message when collapsed and all steps done */}
          {!isExpanded && state.isComplete && (
            <p
              className="select-none px-4 pb-1 text-xs text-muted-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <Trans
                i18nKey="getStarted.allDone"
                components={{
                  1: (
                    <button
                      type="button"
                      data-testid="checklist-finish-close-btn"
                      className="cursor-pointer rounded-sm font-medium text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleComplete();
                      }}
                      disabled={loading}
                    />
                  ),
                }}
              />
            </p>
          )}

          {/* Next step hint when collapsed and not yet complete */}
          {!isExpanded && !state.isComplete && nextStep && (
            <p
              className="cursor-pointer select-none px-4 pb-1 text-xs"
              onClick={() => setIsExpanded(true)}
            >
              <span className="text-muted-foreground">{t('getStarted.nextStep')}</span>{' '}
              <span className="font-medium text-primary underline underline-offset-4">
                {t(nextStep.titleKey)}
              </span>
            </p>
          )}

          {/* Body — white card with border, steps + need help */}
          <CollapsibleContent className="overflow-hidden data-[state=open]:animate-[collapsible-down_200ms_ease-out] data-[state=closed]:animate-[collapsible-up_200ms_ease-out]">
            <div className="rounded-lg border border-border bg-card p-3">
              {/* Steps list */}
              <div className="space-y-1">
                {state.steps.map((step) => (
                  <GetStartedStep
                    key={step.id}
                    step={step}
                    isActive={activeStepId === step.id}
                    onToggle={() => toggleStep(step.id)}
                  />
                ))}
              </div>

              {/* Need help section */}
              <div className="mt-3 flex flex-wrap items-center gap-1 px-2 text-xs select-none">
                <span className="text-muted-foreground">{t('getStarted.needHelp')}</span>
                <Button
                  data-testid="checklist-watch-video-btn"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs underline"
                  onClick={() => setShowVideo(true)}
                >
                  {t('getStarted.watchVideo')}
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      <WistiaDialog
        videoId={showVideo ? GETTING_STARTED_VIDEO_ID : null}
        onClose={() => setShowVideo(false)}
      />
    </div>
  );
}
